import type { ControllerRenderProps } from 'react-hook-form'
import type { ICountry } from 'features/Address'
import type { AddressOptionKey, BranchForForm, Field, OptionProps } from 'types'

import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Dropdown, Loader } from 'components'
import * as utils from 'helpers/getAddress'

interface IInputCountry {
  countries: ICountry[]
  isCountriesLoading: boolean
  field: ControllerRenderProps
  handleOptions: (
    countries: ICountry[],
    name: Field,
  ) => OptionProps<AddressOptionKey>[]
  handleSelected: <T extends { [label: string]: string; id: string }>(
    selectedValue: T,
    name: Field,
  ) => OptionProps<AddressOptionKey> | undefined
  getProvinces: (countryId: string) => void
  emptyId: { id: string }
}

export const InputCountry: React.FC<IInputCountry> = ({
  countries,
  isCountriesLoading,
  field,
  emptyId,
  handleOptions,
  handleSelected,
  getProvinces,
}) => {
  const { errors, setValue, watch } = useFormContext<BranchForForm>()

  const { isSameAsCompany, country } = watch(['isSameAsCompany', 'country'])
  const { t } = useTranslation(['common'])

  const onChange = (item: OptionProps<AddressOptionKey>) => {
    field.onChange(item.key)
    setValue('province', emptyId)
    setValue('district', emptyId)
    setValue('subDistrict', emptyId)
    setValue('postalCode', '')
    getProvinces(item.key.id)
  }

  const isError = utils.handleIsError(
    isSameAsCompany,
    country.id,
    Boolean(errors.country),
  )

  return (
    <Dropdown
      placeholder={t('common:address.country.placeholder')}
      options={handleOptions(countries, 'country')}
      selected={handleSelected(field.value, 'country')}
      onChange={onChange}
      isError={isError}
      withIcon={isCountriesLoading && <Loader />}
      disabled={isSameAsCompany}
      fullWidth
      ref={field.ref}
    />
  )
}
